import { useMemo } from 'react';

import { LinkedAccount } from '../../LinkedAccounts/useSearchedAccounts.query';
import { LinkedApplicationUser } from '../queries/useLinkedApplicationUsers.query';
import { SecurityOverride, SecurityRow, UserAccountRow } from '../UserAccountsSecurity.types';

interface CurrentEmployeeSecurity {
  employeeId: string;
  fullName?: string;
  employerName?: string;
  isPrimaryLinkedAccount?: boolean;
  username?: string;
  lastPasswordUpdate?: string;
  roleType?: SecurityRow['roleType'];
  isLoginLocked?: boolean;
  hasMfa?: boolean;
  isMfaLocked?: boolean;
  mfaLockedTo?: string;
}

interface Props {
  currentEmployee?: CurrentEmployeeSecurity;
  linkedEmployees?: LinkedAccount[];
  applicationUsers?: LinkedApplicationUser[];
  isApplicationUsersEmpty?: boolean;
  overridesByKey?: Record<string, SecurityOverride>;
}

const applyOverrides = (row: SecurityRow, overridesByKey?: Record<string, SecurityOverride>): SecurityRow => {
  const override = overridesByKey?.[row.key];
  if (!override) {
    return row;
  }
  return {
    ...row,
    roleType: override.roleType ?? row.roleType,
    isLoginLocked: override.isLoginLocked ?? row.isLoginLocked,
    isMfaLocked: override.isMfaLocked ?? row.isMfaLocked,
  };
};

const useUserAccountRows = ({
  currentEmployee,
  linkedEmployees,
  applicationUsers,
  isApplicationUsersEmpty,
  overridesByKey,
}: Props) => {
  const accounts = useMemo<UserAccountRow[]>(() => {
    const result: UserAccountRow[] = [];

    if (currentEmployee?.employeeId) {
      result.push({
        employeeId: currentEmployee.employeeId,
        fullName: currentEmployee.fullName || '',
        employerName: currentEmployee.employerName,
        isPrimaryLinkedAccount: currentEmployee.isPrimaryLinkedAccount,
        isCurrentEmployee: true,
      });
    }

    (linkedEmployees || []).forEach((linkedEmployee) => {
      if (!linkedEmployee.employeeId || linkedEmployee.employeeId === currentEmployee?.employeeId) {
        return;
      }
      result.push({
        employeeId: linkedEmployee.employeeId,
        fullName: linkedEmployee.fullName || '',
        employerName: linkedEmployee.employerName,
        isPrimaryLinkedAccount: linkedEmployee.isPrimaryLinkedAccount,
        isCurrentEmployee: false,
      });
    });

    return result;
  }, [currentEmployee, linkedEmployees]);

  const securityRows = useMemo<SecurityRow[]>(() => {
    const notRegisteredRow: SecurityRow[] = currentEmployee?.employeeId
      ? [{
        key: currentEmployee.employeeId,
        isNotRegistered: true,
        isLoginLocked: false,
        hasMfa: false,
        isMfaLocked: false,
      }]
      : [];

    const isRemoved = (row: SecurityRow) => Boolean(overridesByKey?.[row.key]?.isRemoved);

    if (!applicationUsers?.length) {
      if (!currentEmployee?.employeeId) {
        return [];
      }
      if (isApplicationUsersEmpty) {
        return notRegisteredRow;
      }
      const fallbackRow: SecurityRow = {
        key: currentEmployee.username || currentEmployee.employeeId,
        employeeId: currentEmployee.employeeId,
        username: currentEmployee.username,
        lastPasswordUpdate: currentEmployee.lastPasswordUpdate,
        roleType: currentEmployee.roleType,
        isLoginLocked: currentEmployee.isLoginLocked,
        hasMfa: currentEmployee.hasMfa,
        isMfaLocked: currentEmployee.isMfaLocked,
        mfaLockedTo: currentEmployee.mfaLockedTo,
      };
      return isRemoved(fallbackRow) ? notRegisteredRow : [applyOverrides(fallbackRow, overridesByKey)];
    }

    const mappedRows = applicationUsers
      .map((user): SecurityRow => {
        const isCurrentUser = Boolean(user.username && user.username === currentEmployee?.username);
        return {
          key: user.username || user.applicationUserId || '',
          employeeId: isCurrentUser ? currentEmployee?.employeeId : undefined,
          username: user.username,
          lastPasswordUpdate: user.lastPasswordUpdate,
          roleType: user.roleType,
          isLoginLocked: user.isLoginLocked,
          hasMfa: currentEmployee?.hasMfa,
          isMfaLocked: user.isMfaLocked,
          mfaLockedTo: user.mfaLockedTo,
        };
      })
      .filter((row) => !isRemoved(row))
      .map((row) => applyOverrides(row, overridesByKey));

    return mappedRows.length ? mappedRows : notRegisteredRow;
  }, [applicationUsers, currentEmployee, isApplicationUsersEmpty, overridesByKey]);

  const hasLockedByAdmin = useMemo(() => securityRows.some((row) => row.isLoginLocked), [securityRows]);
  const hasLockedByMfa = useMemo(() => securityRows.some((row) => row.isMfaLocked), [securityRows]);

  return {
    accounts,
    securityRows,
    hasLockedByAdmin,
    hasLockedByMfa,
  };
};

export default useUserAccountRows;
